import React, { useState, useEffect } from "react";
import { CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import { ImageWithFallback } from "../../../../Utils/loader";
import { UrduInput } from "../../../../Components/Shared/UrduInput";
import {
  ImageSeoFields,
  appendImageSeoToFormData,
} from "../../../../Components/Shared/ImageSeoFields";
import { UiModal } from "../../../../Components/Shared/UiModal";

const EMPTY_FORM = {
  name: "",
  categoryName_ur: "",
  status: true,
  image: null,
  image_alt: "",
  image_title: "",
  description: "",
  description_ur: "",
  custom_url: "",
};

function InteriorDesignCategoryModal({
  modalOpen,
  toggleModal,
  category,
  handleSaveCategory,
}) {
  const [categoryData, setCategoryData] = useState(EMPTY_FORM);
  const [nameError, setNameError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [imageError, setImageError] = useState("");
  const [customUrlError, setCustomUrlError] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  const revokeBlobPreview = (preview) => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }
  };

  useEffect(() => {
    if (!modalOpen) {
      setImagePreview((prev) => {
        revokeBlobPreview(prev);
        return "";
      });
      setCategoryData(EMPTY_FORM);
      setSelectedImage(null);
      setNameError("");
      setDescriptionError("");
      setImageError("");
      setCustomUrlError("");
      return;
    }

    if (category) {
      setCategoryData({
        name: category.name || "",
        categoryName_ur: category.categoryName_ur || "",
        status: category.status !== undefined ? category.status : true,
        image: category.image || null,
        image_alt: category.image_alt || "",
        image_title: category.image_title || "",
        description: category.description || "",
        description_ur: category.description_ur || "",
        custom_url: category.custom_url || "",
      });
      setSelectedImage(null);
      setImagePreview(category.image_url || category.image || "");
    } else {
      setCategoryData(EMPTY_FORM);
      setSelectedImage(null);
      setImagePreview("");
    }
    setNameError("");
    setDescriptionError("");
    setImageError("");
    setCustomUrlError("");
  }, [category, modalOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryData((prevData) => ({
      ...prevData,
      [name]: value === "true" ? true : value === "false" ? false : value,
    }));
    if (name === "name" && nameError) setNameError("");
    if (name === "custom_url" && customUrlError) setCustomUrlError("");
    if (name === "description" && descriptionError) setDescriptionError("");
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Swal.fire("Error!", "Please select a valid image file.", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("Error!", "Image size should be less than 5MB.", "error");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      if (img.width !== 1200 || img.height !== 900) {
        Swal.fire(
          "Error!",
          "Image resolution must be exactly 1200x900 pixels.",
          "error",
        );
        return;
      }

      revokeBlobPreview(imagePreview);
      setSelectedImage(file);
      setCategoryData((prev) => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
      setImageError("");
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      Swal.fire("Error!", "Failed to load image. Please try again.", "error");
    };
    img.src = objectUrl;
  };

  const removeImage = () => {
    revokeBlobPreview(imagePreview);
    setSelectedImage(null);
    setCategoryData((prev) => ({ ...prev, image: null }));
    setImagePreview("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setNameError("");
    setDescriptionError("");
    setImageError("");
    setCustomUrlError("");

    let hasError = false;

    if (!categoryData.name.trim()) {
      setNameError("Category name is required.");
      hasError = true;
    }

    if (!categoryData.custom_url.trim()) {
      setCustomUrlError("Custom URL is required.");
      hasError = true;
    }

    if (!categoryData.description.trim()) {
      setDescriptionError("Description is required.");
      hasError = true;
    }

    if (!category && !selectedImage && !imagePreview) {
      setImageError("Image is required.");
      hasError = true;
    }

    if (
      category &&
      !selectedImage &&
      !imagePreview &&
      !category.image_url &&
      !category.image
    ) {
      setImageError("Image is required.");
      hasError = true;
    }

    if (hasError) return;

    const formData = new FormData();
    formData.append("name", categoryData.name);
    if (categoryData.categoryName_ur) {
      formData.append("categoryName_ur", categoryData.categoryName_ur);
    }
    formData.append("description", categoryData.description);
    if (categoryData.description_ur) {
      formData.append("description_ur", categoryData.description_ur);
    }
    formData.append("status", categoryData.status);
    formData.append("custom_url", categoryData.custom_url);

    if (selectedImage) {
      formData.append("image", selectedImage);
    }
    appendImageSeoToFormData(
      formData,
      categoryData.image_alt,
      categoryData.image_title,
    );

    handleSaveCategory(formData, category ? category.id : null);
  };

  const handleCloseModal = () => {
    revokeBlobPreview(imagePreview);
    setCategoryData(EMPTY_FORM);
    setSelectedImage(null);
    setImagePreview("");
    setNameError("");
    setDescriptionError("");
    setImageError("");
    setCustomUrlError("");
    toggleModal();
  };

  const isEditMode = Boolean(category);
  const imageRequired =
    !isEditMode || (!category?.image_url && !category?.image);

  return (
    <UiModal
      isOpen={modalOpen}
      onClose={handleCloseModal}
      eyebrow="Interior Design"
      title={isEditMode ? "Edit Category" : "Add Category"}
      as="form"
      onSubmit={handleSubmit}
      footer={
        <>
          <button
            type="button"
            onClick={handleCloseModal}
            className="ui-btn-cancel min-w-[108px]"
          >
            Cancel
          </button>
          <button type="submit" className="ui-btn-save min-w-[108px]">
            {isEditMode ? "Update" : "Save"}
          </button>
        </>
      }
    >
      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Category details</h4>
        <div className="space-y-4">
          <div>
            <label className="ui-form-label">
              Category Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={categoryData.name}
              onChange={handleInputChange}
              placeholder="Enter category name"
              className={`ui-input-field ${
                nameError ? "border-brand-error" : ""
              }`}
            />
            {nameError ? <p className="ui-form-error">{nameError}</p> : null}
            <UrduInput
              label="زمرہ کا نام"
              name="categoryName_ur"
              value={categoryData.categoryName_ur}
              onChange={handleInputChange}
            />
          </div>

          <div>
            <label className="ui-form-label">
              Custom URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="custom_url"
              value={categoryData.custom_url}
              onChange={handleInputChange}
              placeholder="Enter custom URL"
              className={`ui-input-field ${
                customUrlError ? "border-brand-error" : ""
              }`}
            />
            {customUrlError ? (
              <p className="ui-form-error">{customUrlError}</p>
            ) : null}
          </div>

          <div>
            <label className="ui-form-label">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={categoryData.description}
              onChange={handleInputChange}
              placeholder="Enter category description"
              rows={3}
              className={`ui-textarea-field ${
                descriptionError ? "border-brand-error" : ""
              }`}
            />
            {descriptionError ? (
              <p className="ui-form-error">{descriptionError}</p>
            ) : null}
            <UrduInput
              label="تفصیل"
              name="description_ur"
              value={categoryData.description_ur}
              onChange={handleInputChange}
              type="textarea"
              rows={3}
            />
          </div>
        </div>
      </section>

      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Media</h4>
        <div className="space-y-3">
          <label className="ui-form-label">
            Category Image{" "}
            {imageRequired ? <span className="text-red-500">*</span> : null}
          </label>

          <input
            id="interior-category-image"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
          />

          {imagePreview ? (
            <div className="relative">
              <div className="h-48 w-full overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-100">
                <ImageWithFallback
                  src={imagePreview}
                  alt="Category preview"
                  className="h-full w-full object-contain"
                />
              </div>
              <button
                type="button"
                onClick={removeImage}
                className="absolute right-2 top-2 rounded-full bg-red-500 p-2 text-white transition-colors hover:bg-red-600"
                title="Remove image"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
              <label
                htmlFor="interior-category-image"
                className="mt-2 inline-flex cursor-pointer text-sm font-medium text-[#982598] hover:underline"
              >
                Change image
              </label>
            </div>
          ) : (
            <label
              htmlFor="interior-category-image"
              className="flex h-48 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-[#982598]/50 hover:bg-[#982598]/5"
            >
              <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
                <CameraIcon className="h-6 w-6 text-[#982598]" />
              </span>
              <span className="font-medium text-gray-700">Upload Image</span>
              <span className="mt-1 text-sm text-gray-400">
                PNG, JPG, GIF up to 5MB · 1200 × 900
              </span>
            </label>
          )}

          {imageError ? <p className="ui-form-error">{imageError}</p> : null}
          <p className="text-xs text-gray-500">
            Required: exactly 1200 × 900 pixels. Max file size: 5MB
          </p>
          <ImageSeoFields
            imageAlt={categoryData.image_alt}
            imageTitle={categoryData.image_title}
            onAltChange={(value) =>
              setCategoryData((prev) => ({ ...prev, image_alt: value }))
            }
            onTitleChange={(value) =>
              setCategoryData((prev) => ({ ...prev, image_title: value }))
            }
          />
        </div>
      </section>
    </UiModal>
  );
}

export default InteriorDesignCategoryModal;
